import { Spacing } from '@persona-id/design-tokens';
import { ChevronDownIcon, ChevronRightIcon, LoaderIcon } from '@persona-id/design-tokens/icons';
import { FC, useMemo } from 'react';
import styled from 'styled-components';

import { WorkflowRunStepStatus } from 'dashboard/models/workflow-step';
import {
  TimelineProgressBar,
  Variant,
} from 'dashboard/views/DashboardWorkflowRunsShowView/TimelineProgressBar';
import {
  DURATION_COLUMN_WIDTH,
  TimelineViewMode,
} from 'dashboard/views/DashboardWorkflowRunsShowView/WorkflowRunTimeline';
import Button from 'design-system/components/Button';
import Sticker from 'design-system/components/Sticker';
import { TableCell, TableRow } from 'design-system/components/Table';
import { isFocused, isHovered, isNotDisabled } from 'design-system/lib/pseudoClass';
import { focusMixin } from 'design-system/mixins/hoverable';
import { rotate } from 'design-system/variables/keyframes';

const PartitionCell = styled(TableCell).attrs<{ fixedOffset: number }>(({ fixedOffset }) => ({
  style: { left: `${fixedOffset}px` },
}))<{ fixedOffset: number }>`
  padding: 0;
  cursor: col-resize;
  border-right: 1px solid ${({ theme }) => theme.color.border.primary};

  position: sticky;
  z-index: 1;
  background-color: ${({ theme }) => theme.color.background.primary};
`;

const InlineSpacer = styled.span<{ size: number }>`
  display: inline-block;
  width: ${({ size }) => size * 24}px;
`;

const HiddableTableRow = styled(TableRow)<{ hidden: boolean }>`
  ${({ hidden }) => hidden && 'display: none;'}

  cursor: pointer;
`;

const Loader = styled(LoaderIcon)`
  animation: ${rotate} linear 1s infinite;
  color: ${({ theme }) => theme.color.text.secondary};
  margin-left: ${Spacing[4]};
`;

const IconContainer = styled.span<{ hasError: boolean }>`
  vertical-align: middle;
  margin-right: ${Spacing[2]};
  color: ${({ hasError, theme }) =>
    hasError ? theme.color.variants.danger.active : theme.color.text.secondary};
`;

const IconButton = styled(Button)`
  display: inline;
  height: 24px;
  width: 24px;
  background-color: transparent;
  vertical-align: middle;
  color: ${({ theme }) => theme.color.text.secondary};

  &${isHovered}${isNotDisabled} {
    box-shadow: none;
  }
  &${isFocused} {
    ${focusMixin()};
  }
  &${isHovered}${isFocused}${isNotDisabled} {
    ${focusMixin()};
  }
`;

const StickerContainer = styled.span`
  padding: 0 ${Spacing[1]};
`;

const OverflowTableCell = styled(TableCell).attrs<{ hasError: boolean; fixedOffset: number }>(
  ({ fixedOffset }) => ({
    style: {
      left: `${fixedOffset}px`,
    },
  }),
)<{ hasError: boolean; fixedOffset: number }>`
  ${({ hasError, theme }) =>
    hasError &&
    `
    color: ${theme.color.variants.danger.active};`}

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  position: sticky;
  z-index: 1;
  background-color: ${({ theme }) => theme.color.background.primary};
`;

const TimelineCell = styled(TableCell)`
  padding: 0 ${Spacing[2]};
`;

interface TimelineItemProps {
  id: string;
  name?: string;
  icon?: JSX.Element | null;
  duration: string;
  indent: number;
  visible?: boolean;
  childrenVisible: boolean;
  disableToggle?: boolean;
  startFraction: number;
  lengthFraction: number;
  flexibleColumnWidth: number;
  viewMode: TimelineViewMode;
  status: WorkflowRunStepStatus;
  tooltip?: React.ReactNode;
  onSelect: () => void;
  toggleChildren?: (() => void) | null;
  setIsResizingColumns: (isResizing: boolean) => void;
}

export const TimelineItem: FC<TimelineItemProps> = ({
  id,
  name,
  icon,
  duration,
  indent,
  visible = true,
  childrenVisible,
  disableToggle = false,
  startFraction,
  lengthFraction,
  flexibleColumnWidth,
  viewMode,
  status,
  tooltip,
  onSelect,
  toggleChildren,
  setIsResizingColumns,
}) => {
  const variant = useMemo(() => {
    switch (status) {
      case WorkflowRunStepStatus.Errored:
      case WorkflowRunStepStatus.Failed:
        return childrenVisible && !disableToggle ? Variant.ErrorSecondary : Variant.Error;
      case WorkflowRunStepStatus.InProgress:
        return Variant.InProgress;
      default:
        return childrenVisible && !disableToggle ? Variant.Secondary : Variant.Primary;
    }
  }, [status, childrenVisible, disableToggle]);

  const hasError = [WorkflowRunStepStatus.Errored, WorkflowRunStepStatus.Failed].includes(status);

  return (
    <HiddableTableRow
      hidden={!visible}
      id={`workflow-run-diagnostic-step__${id}`}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(evt) => {
        if (evt.key === ' ' || evt.key === 'Enter') {
          evt.preventDefault();
          evt.stopPropagation();
          onSelect();
        }
      }}
    >
      <OverflowTableCell hasError={hasError} fixedOffset={0}>
        <InlineSpacer size={indent} />
        {!disableToggle && toggleChildren ? (
          <IconButton
            id={`workflow-run-diagnostic-step__${id}__toggle-children`}
            onClick={(evt) => {
              toggleChildren();
              evt.stopPropagation();
            }}
            onKeyDown={(evt) => {
              if (evt.key === ' ' || evt.key === 'Enter') {
                evt.preventDefault();
                evt.stopPropagation();
                toggleChildren();
              }
            }}
            aria-label={
              childrenVisible ? `Collapse sub-steps for ${name}` : `Expand sub-steps for ${name}`
            }
            width='icon'
            variant='inverse'
          >
            {childrenVisible ? <ChevronDownIcon /> : <ChevronRightIcon />}
          </IconButton>
        ) : (
          <InlineSpacer size={1} />
        )}
        {icon && <IconContainer hasError={hasError}>{icon}</IconContainer>}
        {name}
        {hasError && (
          <StickerContainer>
            <Sticker aria-label='This step has an error' variant='danger'>
              Error
            </Sticker>
          </StickerContainer>
        )}
      </OverflowTableCell>
      <OverflowTableCell hasError={hasError} fixedOffset={flexibleColumnWidth}>
        {status === WorkflowRunStepStatus.InProgress ? (
          <Loader aria-label='This step is still in progress' />
        ) : (
          duration
        )}
      </OverflowTableCell>
      <PartitionCell
        fixedOffset={flexibleColumnWidth + DURATION_COLUMN_WIDTH}
        id={`workflow-run-diagnostic-step__${id}__column-resize`}
        onMouseDown={() => {
          setIsResizingColumns(true);
        }}
        onMouseUp={() => {
          setIsResizingColumns(false);
        }}
        onClick={(e) => {
          // Prevent click on the surrounding row to expand/collapse/focus the row when dragging
          e.stopPropagation();
        }}
      />
      <TimelineCell>
        <TimelineProgressBar
          start={startFraction}
          length={lengthFraction}
          minLengthFraction={viewMode === 'fit' ? 0.001 : 0}
          variant={variant}
          tooltip={tooltip}
        />
      </TimelineCell>
    </HiddableTableRow>
  );
};
